import {
  Box,
  Grid,
  Theme,
  Stack,
  Container,
  IconButton,
  Typography,
} from "@mui/material";
import Swiper from "swiper";
import {
  ArrowBackIosNewRounded,
  ArrowForwardIosRounded,
} from "@mui/icons-material";
import { useState } from "react";
import { PacksWrapper } from "../Packs/PacksWrapper";
import { FeaturedGameSlider } from "./FeaturedGameSlider";
import { BucketsWrapper } from "../Buckets/BucketsWrapper";
import { Divider } from "@/shared/base/components/Divider/Divider";
import { BrandedCallout } from "../../BrandedCallout/BrandedCallout";
import { RepsProductWarningModal } from "../modals/RepsProductModal";
import { EmptyState } from "@/shared/base/components/EmptyState/EmptyState";
import { PackageSelectionConfirmModal } from "../modals/PackageSelectionConfirmModal";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";
import { usePacks } from "../Packs/usePacks";
import { useFindGames } from "./useFindGames";
import { useBuckets } from "../Buckets/useBuckets";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useDynamicPricing } from "../DynamicPricing/useDynamicPricing";
import type { PacksType } from "@/types";
import type { EventType } from "@/global/store/data/types";
import { CombinedEventCard } from "@/shared/common/EventCard/CombinedEventCard";
import { isRegularEventsMinMaxSelectionReached } from "../utils.events";

export function FindGames() {
  const { theme, teamConfig } = useAppSystem();
  const { uiConfigs, languages } = teamConfig;

  const styles = useStyles(theme);
  const { md: mdDown } = useCssMediaQuery("down");

  const { states, handlers, apis } = useFindGames();
  const {
    regularEvents,
    selectedEvents,
    featuredEvents,
    upcomingEvents,
    selectedPackage,
    isPkgConfirmOpen,
  } = states;

  const {
    availableBuckets,
    isBucketsEnabled,
    handleOpenBucket,
    handleSelectEvent,
    handleDeselectEvent,
  } = useBuckets();

  const {
    packs,
    selectedPack,
    isPacksEnabled,
    handleSelectPack,
    handleOpenPackBucket,
    handleSelectEventForPacks,
    handleDeselectEventForPacks,
  } = usePacks();

  useDynamicPricing();

  const [swiper, setWiper] = useState<Swiper | null>(null);

  const handleOnLoadSldier = (swiper: Swiper) => {
    setWiper(swiper);
  };

  const handleNextSlider = () => {
    swiper?.slideNext();
  };
  const handlePrevSlider = () => {
    swiper?.slidePrev();
  };

  const findGamesTitle = languages?.purchaseSeats?.findGamesTitle || "Featured Games"; // prettier-ignore
  const allUpcomingEventsTitle = languages?.purchaseSeats?.allUpcomingEventsTitle || "All Upcoming Games"; // prettier-ignore
  const noSessionsAvailable = languages?.purchaseSeats?.noSessionsAvailable || "No games available"; // prettier-ignore
  const noSessionsAvailableDescription = languages?.purchaseSeats?.noSessionsAvailableDescription || "Try adjusting your filters."; // prettier-ignore

  const hideAllUpcomingGames = teamConfig?.purchaseSeatConfigs?.hideAllUpcomingGames || false; // prettier-ignore

  const suiteLevelInventoryCardComponents =
    uiConfigs?.inventoryCardSettings?.suiteLevelInventoryCard?.renderBody;

  return (
    <>
      <RepsProductWarningModal />
      <PackageSelectionConfirmModal
        isOpen={!!selectedPackage && isPkgConfirmOpen}
        selectedPackage={selectedPackage}
      />
      {suiteLevelInventoryCardComponents &&
        suiteLevelInventoryCardComponents?.map((item) => (
          <Box key={item.id}>{item.component()}</Box>
        ))}
      <Box width={"100%"} pb={12}>
        <Container maxWidth="xl">
          <Box>
            <BrandedCallout title={uiConfigs?.bannerImage?.title} />
          </Box>

          {(apis.getEvents.isLoading || apis.getPacksMapping.isLoading) && (
            <Box mt={6}>
              <EventsSkeletons />
            </Box>
          )}

          {!apis.getEvents.isLoading && regularEvents.length === 0 && (
            <Stack
              alignItems={"center"}
              justifyContent={"center"}
              height={"340px"}
            >
              <EmptyState
                title={noSessionsAvailable}
                description={noSessionsAvailableDescription}
              />
            </Stack>
          )}

          {!apis.getEvents.isLoading &&
            featuredEvents.length > 0 &&
            !uiConfigs?.hideFeaturedGames && (
              <Box>
                <Box mt={6}>
                  <Divider>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Box
                        bgcolor={theme.palette.background.default}
                        width={"fit-content"}
                        pr={4}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={"700"}
                          color="text.primary"
                        >
                          {findGamesTitle}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          backgroundColor: theme.palette.background.default,
                        }}
                      >
                        <Stack direction={"row"} spacing={1}>
                          <IconButton size="medium" onClick={handlePrevSlider}>
                            <ArrowBackIosNewRounded
                              sx={styles.sliderArrowButton}
                            />
                          </IconButton>
                          <IconButton size="medium" onClick={handleNextSlider}>
                            <ArrowForwardIosRounded
                              sx={styles.sliderArrowButton}
                            />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Stack>
                  </Divider>
                </Box>

                <Box mt={6}>
                  <FeaturedGameSlider
                    onLoad={handleOnLoadSldier}
                    events={featuredEvents}
                    selectedEvents={selectedEvents}
                    onSelect={(ev: EventType) => handlers.handleSelect(ev)}
                    onClickPreview={(ev: EventType) =>
                      handlers.onClickPreview(ev)
                    }
                    isLoading={apis.getEvents.isLoading}
                  />
                </Box>
              </Box>
            )}

          {!apis.getEvents.isLoading &&
          featuredEvents.length > 0 &&
          uiConfigs?.featuredGameCard?.standardEventCard ? (
            <Box mt={6} width={"100%"}>
              <Box>
                <Divider>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Box
                      bgcolor={theme.palette.background.default}
                      width={"fit-content"}
                      pr={4}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={"700"}
                        color="text.primary"
                      >
                        {findGamesTitle}
                      </Typography>
                    </Box>
                  </Stack>
                </Divider>
              </Box>

              <Box mt={6}>
                <Grid container spacing={4}>
                  {featuredEvents?.map((evt, idx) => {
                    const isSelected = selectedEvents.some(
                      (ev) => ev.eventCode == evt.eventCode
                    );
                    const { isMaxEventCountReached } =
                      isRegularEventsMinMaxSelectionReached(
                        selectedEvents,
                        teamConfig
                      );

                    return (
                      <Grid key={idx} item xs={12} md={4} lg={3}>
                        <CombinedEventCard
                          cardVariant={"desktop-mobile-alternate"}
                          variant="featured"
                          event={evt}
                          isDisabled={isMaxEventCountReached && !isSelected}
                          isSelected={isSelected}
                          onSelect={() => handlers.handleSelect(evt)}
                          onClickPreview={() => handlers.onClickPreview(evt)}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          ) : null}

          {!apis.getEvents.isLoading &&
          isBucketsEnabled &&
          !isPacksEnabled &&
          regularEvents.length > 0 &&
          availableBuckets.length > 0 ? (
            <BucketsWrapper
              shouldApplyMargin
              selectedEvents={selectedEvents}
              handleSelect={handleSelectEvent}
              selectedPackage={selectedPackage}
              availableBuckets={availableBuckets}
              handleOpenBucket={handleOpenBucket}
              onClickPreview={handlers.onClickPreview}
              handleDeselectEvent={handleDeselectEvent}
            />
          ) : null}

          {!apis.getEvents.isLoading &&
          !apis.getPacksMapping.isLoading &&
          !isBucketsEnabled &&
          isPacksEnabled &&
          regularEvents.length > 0 &&
          !!selectedPack ? (
            <PacksWrapper
              packs={packs as PacksType}
              selectedPack={selectedPack}
              selectedEvents={selectedEvents}
              selectedPackage={selectedPackage}
              handleSelectPack={handleSelectPack}
              onClickPreview={handlers.onClickPreview}
              handleSelect={handleSelectEventForPacks}
              handleOpenPackBucket={handleOpenPackBucket}
              handleDeselectEvent={handleDeselectEventForPacks}
            />
          ) : null}

          {!apis.getEvents.isLoading &&
            upcomingEvents.length > 0 &&
            !isBucketsEnabled &&
            !hideAllUpcomingGames &&
            !isPacksEnabled && (
              <Box>
                <Box mt={mdDown ? 6 : 12}>
                  <Divider>
                    <Box
                      bgcolor={theme.palette.background.default}
                      width={"fit-content"}
                      pr={4}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={"700"}
                        color="text.primary"
                      >
                        {allUpcomingEventsTitle}
                      </Typography>
                    </Box>
                  </Divider>
                </Box>
                <Box mt={6} width="100%">
                  <Grid container spacing={4}>
                    {upcomingEvents.map((evt, idx) => {
                      const isSelected = selectedEvents.some((ev) => {
                        return ev.eventCode == evt.eventCode;
                      });
                      const { isMaxEventCountReached } =
                        isRegularEventsMinMaxSelectionReached(
                          selectedEvents,
                          teamConfig
                        );

                      return (
                        <Grid key={idx} item xs={12} md={6} lg={4} xl={3}>
                          <CombinedEventCard
                            cardVariant={"desktop-mobile-alternate"}
                            variant="upcoming"
                            event={evt}
                            isDisabled={isMaxEventCountReached && !isSelected}
                            isSelected={isSelected}
                            onClickPreview={() => handlers.onClickPreview(evt)}
                            onSelect={() => handlers.handleSelect(evt)}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>
            )}
        </Container>
      </Box>
    </>
  );
}

export function EventsSkeletons() {
  const { sm: smDown, md: mdDown } = useCssMediaQuery("down");

  return (
    <Box>
      <Grid container spacing={4}>
        {Array.from({ length: smDown ? 2 : mdDown ? 4 : 8 })
          .fill(1)
          .map((itm, idx) => {
            return (
              <Grid key={idx} item xs={12} sm={6} md={4} lg={3}>
                <DynamicSkeleton
                  rowCount={4}
                  spacing={2}
                  widths={["100%", "100%", "80%", "60%"]}
                  heights={["120px", "20px", "20px", "20px"]}
                />
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
}

function useStyles(theme: Theme) {
  return {
    sliderArrowButton: {
      outline: `1px solid ${theme.palette.divider}`,
      width: "40px",
      height: "40px",
      padding: "10px",
      borderRadius: "50%",
    },
  };
}
